import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import api from '../services/api';

/**
 * Whether the platform is charging money right now.
 *
 * One admin switch (Admin > Membership Settings > Enforce Pricing) decides it.
 * When it is off the product is free and every price, plan card and upgrade CTA
 * is hidden — companies that already subscribed keep their subscription and keep
 * being billed, so the Billing page still shows *their* plan.
 *
 * The flag is public (no auth) because logged-out pages need it too.
 */
const PricingContext = createContext({
  enforced: false,
  loading: true,
  billing: null,
  refresh: () => {},
});

export const PricingProvider = ({ children }) => {
  const [enforced, setEnforced] = useState(false);
  const [loading, setLoading] = useState(true);
  const [billing, setBilling] = useState(null);

  const refresh = useCallback(async () => {
    try {
      const res = await api.get('/pricing-enforcement');
      setEnforced(!!res.data?.enforced);
    } catch (err) {
      // Default to "free platform" if the flag cannot be read: showing prices
      // that are not being charged is worse than hiding prices for a moment.
      setEnforced(false);
    } finally {
      setLoading(false);
    }

    if (localStorage.getItem('token')) {
      try {
        const res = await api.get('/billing-status');
        setBilling(res.data);
      } catch (err) {
        setBilling(null);
      }
    } else {
      setBilling(null);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  return (
    <PricingContext.Provider value={{ enforced, loading, billing, refresh }}>
      {children}
    </PricingContext.Provider>
  );
};

export const usePricing = () => useContext(PricingContext);

export default PricingContext;
